import { useCallback, useEffect, useState } from "react";
import {
  Stack,
  Table,
  Tabs,
  Tab,
  Box,
  Typography,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  TableHeadCustom,
  TableNoData,
  useTable,
} from "../../../components/table";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import Scrollbar from "../../../components/scrollbar";
import SignatureDialog from "../../../components/signature/SignatureDialog";
import { CustomAvatar } from "../../../components/custom-avatar";
import PatientInfo from "./PatientInfo";
import PatientPrescreening from "./PatientPrescreening";
import PatientCommunicationLog from "./PatientCommunicationLog";
import { ClinicalRecords } from "../../../api/clinicalRecords.service";
import { formatDate } from "@fullcalendar/core";
import { fDate } from "../../../utils/formatTime";
import Refraction from "./Refraction/Refraction";
import AddHistoryAndSymptoms from "./HistoryAndSymptoms/AddHistoryAndSymptoms";
import { useParams, useLocation } from "react-router-dom";
import { PatientService } from "../../../api/patient.service";
import { HistoryAndSymptomsService } from "../../../api/historyAndSymptoms.service";
import Examination from "./Examination/Examination";
import Handover from "./Handover/Handover";
import { HandoverService } from "../../../api/handover.service";
import CLHistoryAndSymptoms from "./ContactLenses/CLHistoryAndSymptoms/CLHistoryAndSymptoms";
import AnteriorEye from "./ContactLenses/AnteriorEye/AnteriorEye";
import CLFitting from "./ContactLenses/CLFitting/CLFitting";
import CLAftercare from "./ContactLenses/CLAftercare/CLAftercare";
import TeachScreen from "./ContactLenses/TeachScreen/TeachScreen";
import { ICLHistoryAndSymptoms } from "../../../@types/clHistoryAndSymptoms.type";
import { CLHistoryAndSymptomsService } from "../../../api/contact-lenses/cl-history-and-symptoms.service";
import { IClinicalRecordSchema } from "../../../@types/clinicalRecord.type";
import { IRefractionSchema } from "../../../@types/refraction";
import { IExaminationSchema } from "../../../@types/examination";
import { IHandoverSchema } from "../../../@types/handover";
import { IPatientSymptomsSchema } from "../../../@types/patientSymptoms";
import { IPatientInfo } from "../../../@types/patientInfo.type";
import { AnteriorEyeService } from "../../../api/contact-lenses/anterior-eye.service";
import { IAnteriorEye } from "../../../@types/anteriorEye.type";
import CLHandover from "./ContactLenses/Handover/CLHandover";
import { CLFittingService } from "../../../api/contact-lenses/cl-fitting.service";
import { ICLFitting } from "../../../@types/clFitting.type";
import { TrailsService } from "../../../api/contact-lenses/trail.service";
import { ITrail } from "../../../@types/trail.type";
import { CLAftercareService } from "../../../api/contact-lenses/cl-aftercare.service";
import { ICLAftercare } from "../../../@types/clAftercare.type";
import { CLHandoverService } from "../../../api/contact-lenses/cl-handover.service";
import { ICLHandover } from "../../../@types/clHandover.type";
import { TeachScreenService } from "../../../api/contact-lenses/teachscreen.service";
import { ITeachScreen } from "../../../@types/teachscreen.type";
import {
  IAutoRefractorSchema,
  IVisionSchema,
} from "../../../@types/specsHistory";
import { useAppContext } from "../../../providers/AppContext";
import DiabeticEyeScreeningForm from "./ClinalPathways/DiabeticEyeScreeningForm";
import MecsCuesForm from "./ClinalPathways/MecsCuesForm";
import GlaucomaReferralRefinementForm from "./ClinalPathways/GlaucomaReferralRefinementForm";
import ContactLensTeachingForm from "./ClinalPathways/ContactLensTeachingForm";
import HandoverOutcomeForm from "./ClinalPathways/HandoverOutcomeForm";
import { DEService } from "../../../api/clinical-pathways/des.service";
import { MecsCuesService } from "../../../api/clinical-pathways/mecs-cues.service";
import { GlaucomaReferralRefinementService } from "../../../api/clinical-pathways/grr.service";

const EYE_EXAMINATION_TABS = [
  { id: "info", label: "Info", align: "left" },
  { id: "prescreening", label: "Prescreening", align: "left" },
  { id: "history_and_symptoms", label: "History And Symptoms", align: "left" },
  {
    id: "refraction",
    label: "Refraction",
    align: "left",
  },
  { id: "examination", label: "Examination", align: "center" },
  { id: "handover", label: "Handover", align: "center" },
  { id: "communications", label: "Communications", align: "left" },
];

const CONTACT_LENSES_TABS = [
  { id: "info", label: "Info", align: "left" },
  { id: "cl_h_and_s", label: "CL H&S", align: "left" },
  { id: "anterior_eye", label: "Anterior Eye", align: "left" },
  {
    id: "cl_fitting",
    label: "CL Fitting",
    align: "left",
  },
  { id: "cl_aftercare", label: "CL Aftercare", align: "center" },
  { id: "cl_handover", label: "Handover", align: "center" },
  { id: "teach_screen", label: "Teach Screen", align: "center" },
  { id: "communications", label: "Communications", align: "left" },
];

const CLINICAL_PATHWAY_TABS = [
  { id: "info", label: "Info", align: "left" },
  { id: "des", label: "Diabetic Eye Screening" },
  { id: "mecs", label: "MECS / CUES" },
  { id: "grr", label: "Glaucoma Referral Refinement" },
  // { id: "cl_teaching", label: "Contact Lens Teaching" },
  { id: "handover_outcome", label: "Handover & Outcome", align: "center" },
  { id: "communications", label: "Communications", align: "left" },
];

const TABLE_HEAD = [
  { id: "id", label: "ID", align: "left" },
  {
    id: "reasonForVisit",
    label: "Visit Reason",
    align: "left",
  },
  {
    id: "type",
    label: "Type",
    align: "left",
  },
  { id: "createdDate", label: "Created Date", align: "left" },
];

export default function PatientClinicalRecords() {
  // ------------------------------
  // 📌 TABLE & ROUTER
  // ------------------------------
  const { dense } = useTable({ defaultOrderBy: "startTime" });
  const location = useLocation();
  const { patientId } = useParams();


  // ------------------------------
  // 📌 CONTEXT
  // ------------------------------
  const { getAllClinicalRecords, clinicalRecords } = useAppContext();
  const { enqueueSnackbar } = useSnackbar();

  // ------------------------------
  // 📌 PRIMARY UI STATE
  // ------------------------------
  const [eyeExamination, setEyeExamination] = useState("info");
  const [contactLenses, setContactLenses] = useState("info");
  const [diabeticEyeScreening, setDiabeticEyeScreening] = useState("info");
  const [selectedType, setSelectedType] = useState<string>("eye_examination");
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [signingOffId, setSigningOffId] = useState<number | null>(null);
  const [openSignatureDialog, setOpenSignatureDialog] = useState(false);

  // ------------------------------
  // 📌 PATIENT INFO
  // ------------------------------
  const [patientInfo, setPatientInfo] = useState<IPatientInfo>();



  // ------------------------------
  // 📌 CLINICAL RECORDS (DATA STATES)
  // ------------------------------
  const [selectedClinicalRecord, setSelectedClinicalRecord] = useState<any>({});
  const [clinicalRecordId, setClinicalRecordId] = useState<number>();
  const [desRecord, setDesRecord] = useState<any>({});

  // ------------------------------
  // 📌 EYE EXAMINATION STATES
  // ------------------------------
  const [refraction, setRefraction] = useState({} as IRefractionSchema);
  const [visionData, setVisionData] = useState({
    nearVision: {} as IVisionSchema,
    distanceVision: {} as IVisionSchema,
  });
  const [autoRefractorData, setAutoRefractorData] = useState(
    {} as IAutoRefractorSchema
  );
  const [examination, setExamination] = useState({} as IExaminationSchema);
  const [handover, setHandover] = useState({} as IHandoverSchema);
  const [history, setHistory] = useState({} as IPatientSymptomsSchema);
  const [anteriorEye, setAnteriorEye] = useState({} as IAnteriorEye);

  // ------------------------------
  // 📌 CONTACT LENS STATES
  // ------------------------------
  const [showCLForm, setShowCLForm] = useState(false);
  const [clHistoryAndSymptoms, setClHistoryAndSymptoms] = useState(
    {} as ICLHistoryAndSymptoms
  );
  const [clFitting, setClFitting] = useState({} as ICLFitting);
  const [trails, setTrails] = useState<ITrail[]>([]);
  const [clAftercare, setClAftercare] = useState({} as ICLAftercare);
  const [clHandover, setClHandover] = useState({} as ICLHandover);
  const [teachScreen, setTeachScreen] = useState({} as ITeachScreen);

  // ------------------------------
  // 📌 MISC / PAYLOAD
  // ------------------------------
  const [payloadState, setPayloadState] = useState<any>(null);
  const [finalData, setFinalData] = useState({});

  // ------------------------------
  // 📌 NOTES DIALOG STATE
  // ------------------------------
  const [openNotesDialog, setOpenNotesDialog] = useState(false);
  const [notesContent, setNotesContent] = useState<string>("");

  // ======================================================
  // 🟦 EFFECT: SHOW NOTES DIALOG IF NAV STATE REQUESTED
  // ======================================================
  useEffect(() => {
    const navState: any = location?.state;
    if (
      navState?.showNotes &&
      typeof navState?.notes === "string" &&
      navState.notes.trim().length > 0
    ) {
      setNotesContent(navState.notes);
      const timer = setTimeout(() => setOpenNotesDialog(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [location]);

  const handleCloseNotesDialog = () => setOpenNotesDialog(false);

  // ======================================================
  // 🟩 EFFECT: LOAD PATIENT INFO
  // ======================================================
  useEffect(() => {
    if (patientId) {
      const fetchPatientInfo = async () => {
        try {
          const patientService = new PatientService();
          const response = await patientService.getPatientName(patientId);
          setPatientInfo(response.data);
        } catch (error) {
          console.error("Error fetching patient data:", error);
        }
      };
      fetchPatientInfo();
    }
  }, [patientId]);
  // ======================================================
  // 🟧 EFFECT: LOAD CLINICAL RECORDS
  // ======================================================
  useEffect(() => {
    if (patientId) getAllClinicalRecords(patientId);
  }, [patientId]);

  // ======================================================
  // 🔧 HANDLERS
  // ======================================================
  const handlePayloadUpdate = (payload: any) => {
    setPayloadState(payload);
  };

  const handleChildChange = (event: React.SyntheticEvent, newValue: string) =>
    setEyeExamination(newValue);

  const handleContactLensChange = (
    event: React.SyntheticEvent,
    newValue: string
  ) => setContactLenses(newValue);

  const handleDiabeticEyeScreeningChange = (
    event: React.SyntheticEvent,
    newValue: string
  ) => setDiabeticEyeScreening(newValue);

  const handleTabChange = (tabId: string) => {
    setDiabeticEyeScreening(tabId);
  };

  const setNewClinicalRecordId = (id: string) => {
    if (selectedRow !== id) {
      setSelectedRow(id);
    }
  };

  const getTabLabel = (tab: any, type: string) => {
    if (!selectedClinicalRecord || !clinicalRecords || tab.id === 'info' || tab.id === 'communications') return tab.label;

    const currentRecord = (clinicalRecords as IClinicalRecordSchema[]).find(r => r.id === selectedRow);
    if (!currentRecord) return tab.label;

    let hasData = false;
    const record = currentRecord as any;
    if (type === 'eye_examination') {
      switch (tab.id) {
        case 'prescreening': hasData = !!record.prescreeningId; break;
        case 'history_and_symptoms': hasData = !!record.symptomsId; break;
        case 'refraction': hasData = !!record.refractionId; break;
        case 'examination': hasData = !!record.examinationId; break;
        default: break;
      }
    } else if (type === 'contact_lenses') {
      switch (tab.id) {
        case 'cl_h_and_s': hasData = !!record.clHistoryAndSymptomsId; break;
        case 'anterior_eye': hasData = !!record.anteriorEyeId; break;
        case 'cl_fitting': hasData = !!record.contactLensFittingId; break;
        case 'cl_aftercare': hasData = !!record.contactLensAfterCareId; break;
        case 'teach_screen': hasData = !!record.teachScreenId; break;
        default: break;
      }
    } else if (type === 'diabetic_eye_screening') {
      switch (tab.id) {
        case 'des': hasData = !!record.diabeticEyeScreeningId; break;
        case 'mecs': hasData = !!record.mecsCuesId; break;
        case 'grr': hasData = !!record.glaucomaReferralRefinementId; break;
        default: break;
      }
    }

    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="inherit">{tab.label}</Typography>
        <Typography variant="inherit" sx={{ ml: 0.5 }}>
          {hasData ? '✓' : '○'}
        </Typography>
      </Stack>
    );
  };

  const showEyeExaminationTabs = () => {
    switch (eyeExamination) {
      case EYE_EXAMINATION_TABS[0].id:
        return patientInfo && <PatientInfo patientInfo={patientInfo} />;
      case EYE_EXAMINATION_TABS[1].id:
        return (
          <PatientPrescreening
            tab={(tab: string) => setEyeExamination(tab)}
            clinicalRecordId={selectedRow}
            data={selectedClinicalRecord}
            setNewClinicalId={setNewClinicalRecordId}
            onUpdate={getAllClinicalRecords}
            onUpdatePayload={handlePayloadUpdate}
            setSelectedType={(type: string) => {
              setSelectedType(type);
              setSelectedRow(null);
              setClinicalRecordId(undefined);
              setSelectedClinicalRecord({});
              setDesRecord({});
              setMecsRecord({});
              setGrrRecord({});
              setEyeExamination("info");
              setContactLenses("info");
              setDiabeticEyeScreening("info");
              // Reset other Eye Exam states if necessary
              setHistory({} as any);
              setExamination({} as any);
              setRefraction({} as any);
              setHandover({} as any);
            }}
            setShowCLForm={(showCLForm: boolean) => setShowCLForm(showCLForm)}
            setHistory={(history: IPatientSymptomsSchema) =>
              setHistory(history)
            }
          />
        );
      case EYE_EXAMINATION_TABS[2].id:
        return (
          <AddHistoryAndSymptoms
            history={history}
            clinicalRecordId={selectedRow}
            tab={(tab: string) => setEyeExamination(tab)}
            getAllHistoryAndSymptoms={getHistoryRecords}
            setHistory={(data: IPatientSymptomsSchema) => {
              setHistory(data);
            }}
          />
        );
      case EYE_EXAMINATION_TABS[3].id:
        return (
          <Refraction
            tab={(tab: string) => setEyeExamination(tab)}
            selectedRefraction={refraction}
            clinicalRecordId={selectedRow}
            getAllRefraction={getRefractionRecords}
            visionData={visionData}
            autoRefractorData={autoRefractorData}
            setRefraction={(data: IRefractionSchema) => {
              setRefraction(data);
            }}
          />
        );
      case EYE_EXAMINATION_TABS[4].id:
        return (
          <Examination
            setTab={(tab: string) => setEyeExamination(tab)}
            clinicalRecordId={selectedRow}
            selectedExamination={examination}
            prescreeningData={selectedClinicalRecord}
            getAllExamination={getExaminationRecords}
            finalData={finalData}
            setExamination={(data: IExaminationSchema) => {
              setExamination(data);
            }}
          />
        );
      case EYE_EXAMINATION_TABS[5].id:
        const currentRecord = clinicalRecords.find(
          (record) => record.id === selectedRow
        );
        return (
          <Handover
            selectedHandover={handover}
            clinicalRecordId={selectedRow}
            clinicalRecord={currentRecord}
            getAllHandover={getHandoverRecords}
            onUpdate={() => getAllClinicalRecords(patientId)}
            finalData={finalData}
            setHandover={(data: IHandoverSchema) => {
              setHandover(data);
            }}
          />
        );
      case "communications":
        return <PatientCommunicationLog patientId={patientId!} />;
      default:
        break;
    }
  };

  const showContactLensesTabs = () => {
    switch (contactLenses) {
      case CONTACT_LENSES_TABS[0].id:
        // return patientInfo && <PatientInfo patientInfo={patientInfo} />;
        return <PatientInfo patientInfo={patientInfo} />;
      case CONTACT_LENSES_TABS[1].id:
        return (
          <CLHistoryAndSymptoms
            selectedType={selectedType}
            clHistorySymptoms={clHistoryAndSymptoms}
            setSelectedType={(selectedType: string) =>
              setSelectedType(selectedType)
            }
            showCLForm={showCLForm}
            tab={(tab: string) => setContactLenses(tab)}
            onUpdate={getAllClinicalRecords}
            setClinicalRecordId={(id: number) => setClinicalRecordId(id)}
            getAllCLHistoryAndSymptoms={getAllCLHistoryAndSymptoms}
            setSelectedRow={(selectedRow: string) =>
              setSelectedRow(selectedRow)
            }
            clinicalRecordId={selectedRow}
            finalData={finalData}
          />
        );
      case CONTACT_LENSES_TABS[2].id:
        return (
          <AnteriorEye
            anteriorEye={anteriorEye}
            clinicalRecordId={clinicalRecordId ? clinicalRecordId : selectedRow}
            tab={(tab: string) => setContactLenses(tab)}
            showCLForm={showCLForm}
            getAnteriorEyeRecords={getAnteriorEyeRecords}
            selectedRow={selectedRow}
            clinicalRecords={clinicalRecords}
          />
        );
      case CONTACT_LENSES_TABS[3].id:
        return (
          <CLFitting
            clinicalRecordId={clinicalRecordId ? clinicalRecordId : selectedRow}
            tab={(tab: string) => setContactLenses(tab)}
            showCLForm={showCLForm}
            clFitting={clFitting}
            trails={trails}
            getCLFittingRecords={getCLFittingRecords}
            getTrailsRecords={getTrailsRecords}
            selectedRow={selectedRow}
            clinicalRecords={clinicalRecords}
          />
        );
      case CONTACT_LENSES_TABS[4].id:
        return (
          <CLAftercare
            clinicalRecordId={clinicalRecordId ? clinicalRecordId : selectedRow}
            tab={(tab: string) => setContactLenses(tab)}
            clAftercare={clAftercare}
            showCLForm={showCLForm}
            getCLAftercareRecords={getCLAftercareRecords}
            selectedRow={selectedRow}
            clinicalRecords={clinicalRecords}
            trails={trails}
          />
        );
      case CONTACT_LENSES_TABS[5].id:
        const currentRecord = clinicalRecords.find(
          (record) => record.id === selectedRow
        );
        return (
          <CLHandover
            clinicalRecordId={clinicalRecordId ? clinicalRecordId : selectedRow}
            tab={(tab: string) => setContactLenses(tab)}
            clHandover={clHandover}
            showCLForm={showCLForm}
            getCLHandoverRecords={getCLHandoverRecords}
            clinicalRecord={currentRecord}
            onUpdate={() => getAllClinicalRecords(patientId)}
            finalData={finalData}
          />
        );
      case CONTACT_LENSES_TABS[6].id:
        return (
          <TeachScreen
            clinicalRecordId={clinicalRecordId ? clinicalRecordId : selectedRow}
            teachScreen={teachScreen}
            showCLForm={showCLForm}
            getTeachScreenRecords={getTeachScreenRecords}
          />
        );
      case "communications":
        return <PatientCommunicationLog patientId={patientId!} />;
      default:
        break;
    }
  };

  // function to show different tab data
  const showDiabeticEyeScreeningTabs = () => {
    switch (diabeticEyeScreening) {
      case CLINICAL_PATHWAY_TABS[0].id:
        return patientInfo && <PatientInfo patientInfo={patientInfo} />;

      case CLINICAL_PATHWAY_TABS[1].id:
        return (
          <DiabeticEyeScreeningForm
            clinicalRecordId={clinicalRecordId ? clinicalRecordId : selectedRow}
            desRecord={desRecord}
            onUpdate={() => getAllClinicalRecords(patientId)}
          />
        );

      case CLINICAL_PATHWAY_TABS[2].id:
        return (
          <MecsCuesForm
            clinicalRecordId={clinicalRecordId ? clinicalRecordId : selectedRow}
            mecsRecord={mecsRecord}
            onUpdate={() => getAllClinicalRecords(patientId)}
          />
        );

      case CLINICAL_PATHWAY_TABS[3].id:
        return (
          <GlaucomaReferralRefinementForm
            clinicalRecordId={clinicalRecordId ? clinicalRecordId : selectedRow}
            grrRecord={grrRecord}
            onUpdate={() => getAllClinicalRecords(patientId)}
          />
        );

      // case CLINICAL_PATHWAY_TABS[4].id:
      //   return <ContactLensTeachingForm />;

      case CLINICAL_PATHWAY_TABS[4].id:
        const currentRecord = clinicalRecords.find(
          (record) => record.id === selectedRow
        );
        return (
          <HandoverOutcomeForm
            clinicalRecordId={clinicalRecordId ? clinicalRecordId : selectedRow}
            desRecord={desRecord}
            onUpdate={() => getAllClinicalRecords(patientId)}
            clinicalRecord={currentRecord}
          />
        );

      case "communications":
        return <PatientCommunicationLog patientId={patientId!} />;
      default:
        return null;
    }
  };

  const getAllCLHistoryAndSymptoms = async (
    clHistoryId: number,
    clinicalId: number
  ) => {
    if (clHistoryId === null) {
      setClHistoryAndSymptoms({} as ICLHistoryAndSymptoms);
      return;
    }
    const clHistoryAndSymptomsService = new CLHistoryAndSymptomsService();

    try {
      const res = await clHistoryAndSymptomsService.getHistoryAndSymptoms(
        patientId!,
        clHistoryId
      );
      const data = res.data[0];
      setClHistoryAndSymptoms(data);
    } catch (error) {
      console.error("Error fetching CL history and symptoms:", error);
      setClHistoryAndSymptoms({} as ICLHistoryAndSymptoms);
    }
  };

  const getAnteriorEyeRecords = async (
    anteriorEyeId: number | null,
    clinicalId: number
  ) => {
    if (anteriorEyeId === null) {
      setAnteriorEye({} as IAnteriorEye);
      return;
    }
    const anteriorEyeService = new AnteriorEyeService();

    try {
      const res = await anteriorEyeService.getAnteriorEye(
        patientId!,
        anteriorEyeId
      );
      const data = res.data[0];
      setAnteriorEye(data);
    } catch (error) {
      console.error("Error fetching CL anteriorEye:", error);
      setAnteriorEye({} as IAnteriorEye);
    }
  };

  const getCLFittingRecords = async (
    clFittingId: number | null,
    clinicalId: number
  ) => {
    if (clFittingId === null) {
      setClFitting({} as ICLFitting);
      return;
    }

    const clFittingService = new CLFittingService();

    try {
      const res = await clFittingService.getCLFittings(patientId!, clFittingId);
      const data = res.data[0];
      setClFitting(data);
    } catch (error) {
      console.error("Error fetching CL fitting records:", error);
      setClFitting({} as ICLFitting);
    }
  };

  const getTrailsRecords = async (clinicalId: number) => {
    if (selectedType === "contact_lenses") {
      const trailsService = new TrailsService();

      try {
        const res = await trailsService.getTrails(patientId!, clinicalId);
        const data = res.data;
        setTrails(data);
      } catch (error) {
        console.error("Error fetching trails records:", error);
        setTrails([]);
      }
    }
  };

  const getCLAftercareRecords = async (
    clAftercareId: number | null,
    clinicalId: number
  ) => {
    if (clAftercareId === null) {
      setClAftercare({} as ICLAftercare);
      return;
    }
    const clAftercareService = new CLAftercareService();

    try {
      const res = await clAftercareService.getCLAftercare(
        patientId!,
        clAftercareId
      );
      const data = res.data[0];
      setClAftercare(data);
    } catch (error) {
      console.error("Error fetching CL aftercare records:", error);
      setClAftercare({} as ICLAftercare);
    }
  };

  const getCLHandoverRecords = async (
    clHandoverId: number | null,
    clinicalId: number
  ) => {
    if (clHandoverId === null) {
      setClHandover({} as ICLHandover);
      return;
    }
    const clHandoverService = new CLHandoverService();

    try {
      const res = await clHandoverService.getCLHandover(
        patientId!,
        clHandoverId
      );
      const data = res.data[0];
      setClHandover(data);
    } catch (error) {
      console.error("Error fetching CL handover records:", error);
      setClHandover({} as ICLHandover);
    }
  };

  const getTeachScreenRecords = async (
    teachScreenId: number | null,
    clinicalId: number
  ) => {
    if (teachScreenId === null) {
      setTeachScreen({} as ITeachScreen);
      return;
    }
    const teachScreenService = new TeachScreenService();

    try {
      const res = await teachScreenService.getTeachScreen(
        patientId!,
        teachScreenId
      );
      const data = res.data[0];
      setTeachScreen(data);
    } catch (error) {
      console.error("Error fetching teach screen records:", error);
      setTeachScreen({} as ITeachScreen);
    }
  };

  const getDESRecords = async (desId: number | null, clinicalId: number) => {
    if (desId === null) {
      setDesRecord({});
      return;
    }
    const desService = new DEService();
    try {
      const res = await desService.getDES(patientId!, desId);
      const data = res.data[0];
      setDesRecord(data || {});
    } catch (error) {
      console.error('Error fetching DES records:', error);
      setDesRecord({});
    }
  };

  const [mecsRecord, setMecsRecord] = useState<any>({});
  const [grrRecord, setGrrRecord] = useState<any>({});

  const getMECSRecords = async (mecsId: number | null, clinicalId: number) => {
    if (mecsId === null) {
      setMecsRecord({});
      return;
    }
    const mecsService = new MecsCuesService();
    try {
      const res = await mecsService.getMecs(patientId!, mecsId);
      const data = res.data[0];
      setMecsRecord(data || {});
    } catch (error) {
      console.error('Error fetching MECS/CUES records:', error);
      setMecsRecord({});
    }
  };

  const getGRRRecords = async (grrId: number | null, clinicalId: number) => {
    if (grrId === null) {
      setGrrRecord({});
      return;
    }
    const grrService = new GlaucomaReferralRefinementService();
    try {
      const res = await grrService.get(patientId!, grrId);
      const data = res.data[0];
      setGrrRecord(data || {});
    } catch (error) {
      console.error('Error fetching GRR records:', error);
      setGrrRecord({});
    }
  };

  const getClinicalRecord = useCallback(
    async (prescreeningId: number | null, clinicalId: number) => {
      if (prescreeningId === null) {
        setSelectedClinicalRecord({});
        return;
      }
      const clinicalRecords = new ClinicalRecords();

      try {
        setVisionData({
          nearVision: {} as IVisionSchema,
          distanceVision: {} as IVisionSchema,
        });
        const res = await clinicalRecords.getPrescreeningById(
          patientId!,
          prescreeningId
        );
        const data = res.data[0];

        setAutoRefractorData(data?.autoRefractor);
        const specsHistory = data?.specificationHistories;
        if (specsHistory) {
          specsHistory.forEach((spec: any) => {
            if (spec?.specifications === "SVN") {
              setVisionData((prevData) => ({
                ...prevData,
                nearVision: {
                  rightSphericalPower: spec?.rightSphericalPower,
                  leftSphericalPower: spec?.leftSphericalPower,
                  rightCylinder: spec?.rightCylinder,
                  leftCylinder: spec?.leftCylinder,
                  rightAxis: spec?.rightAxis,
                  leftAxis: spec?.leftAxis,
                  rightDistancePD: spec?.rightDistancePD,
                  leftDistancePD: spec?.leftDistancePD,
                  rightBVD: spec?.rightBVD,
                  leftBVD: spec?.leftBVD,
                },
              }));
            } else if (spec?.specifications === "SVD") {
              setVisionData((prevData) => ({
                ...prevData,
                distanceVision: {
                  rightSphericalPower: spec?.rightSphericalPower,
                  leftSphericalPower: spec?.leftSphericalPower,
                  rightCylinder: spec?.rightCylinder,
                  leftCylinder: spec?.leftCylinder,
                  rightAxis: spec?.rightAxis,
                  leftAxis: spec?.leftAxis,
                  rightDistancePD: spec?.rightDistancePD,
                  leftDistancePD: spec?.leftDistancePD,
                  rightBVD: spec?.rightBVD,
                  leftBVD: spec?.leftBVD,
                },
              }));
            }
          });
        }
        setSelectedClinicalRecord(data);
      } catch (error) {
        console.error("Error fetching Prescreening Records:", error);
        setSelectedClinicalRecord({});
      }
    },
    [patientId]
  );

  const getAllClinicsRecords = useCallback(async () => {
    if (!clinicalRecords || clinicalRecords.length === 0) {
      return; // Wait for clinical records to be loaded
    }

    const clinicalId = clinicalRecords[0]?.id;
    const prescreeningId = clinicalRecords[0]?.prescreeningId;
    const historyId = clinicalRecords[0]?.symptomsId;
    const refractionId = clinicalRecords[0]?.refractionId;
    const examinationId = clinicalRecords[0]?.examinationId;
    const handoverId = clinicalRecords[0]?.handoverId;
    const desId = clinicalRecords[0]?.diabeticEyeScreeningId;
    const mecsId = clinicalRecords[0]?.mecsCuesId;
    const grrId = clinicalRecords[0]?.glaucomaReferralRefinementId;



    if (!selectedRow || !clinicalRecords.some((record: any) => record.id === selectedRow)) {
      setSelectedRow(clinicalId);
    }

    // Use clinicalId directly instead of selectedRow to avoid stale closure
    await Promise.all([
      getClinicalRecord(prescreeningId, clinicalId),
      getHistoryRecords(historyId, clinicalId),
      getRefractionRecords(refractionId, clinicalId),
      getExaminationRecords(examinationId, clinicalId),
      getHandoverRecords(handoverId, clinicalId),
      getDESRecords(desId ?? null, clinicalId),
      getMECSRecords(mecsId ?? null, clinicalId),
      getGRRRecords(grrId ?? null, clinicalId),
    ]);
  }, [clinicalRecords, getClinicalRecord]); // Add dependencies

  const getRefractionRecords = async (
    refractionId: number | null,
    clinicalId: number
  ) => {
    if (refractionId === null) {
      setRefraction({} as IRefractionSchema);
      return;
    }
    const refractionService = new ClinicalRecords();

    try {
      const res = await refractionService.getRefractionById(
        patientId!,
        refractionId
      );
      const data = res.data[0];
      setFinalData(data?.final);
      setRefraction(data);
    } catch (error) {
      console.error("Error fetching Refraction Records:", error);
      setRefraction({} as IRefractionSchema);
    }
  };

  const getExaminationRecords = async (
    examinationId: number | null,
    clinicalId: number
  ) => {
    if (examinationId === null) {
      setExamination({} as IExaminationSchema);
      return;
    }
    const clinicalRecords = new ClinicalRecords();

    try {
      const res = await clinicalRecords.getExaminationByClinicalRecordId(
        patientId!,
        examinationId
      );
      const data = res.data[0];
      setExamination(data);
    } catch (error) {
      console.error("Error fetching Examination:", error);
      setExamination({} as IExaminationSchema);
    }
  };

  const getHandoverRecords = async (
    handoverId: number | null,
    clinicalId: number
  ) => {
    if (handoverId === null) {
      setHandover({} as IHandoverSchema);
      return;
    }
    const handoverRecords = new HandoverService();

    try {
      const res = await handoverRecords.getAllHandoverByClinicalRecordId(
        patientId!,
        handoverId
      );
      const data = res.data[0];
      setHandover(data);
    } catch (error) {
      console.error("Error fetching Handover:", error);
      setHandover({} as IHandoverSchema);
    }
  };

  const getHistoryRecords = async (
    symptomsId: number | null,
    clinicalId: number
  ) => {
    if (symptomsId === null) {
      setHistory({} as IPatientSymptomsSchema);
      return;
    }
    const historyRecord = new HistoryAndSymptomsService();

    try {
      const res = await historyRecord.getHistoryAndSymptoms(
        patientId!,
        symptomsId
      );
      setHistory(res.data);
    } catch (error) {
      console.error("Error fetching H&S:", error);
      setHistory({} as IPatientSymptomsSchema);
    }
  };

  const handleSignOffConfirm = async (signatureData: string) => {
    if (!selectedRow) return;
    setSigningOffId(selectedRow);
    try {
      const service = new ClinicalRecords();
      await service.signOff(selectedRow, signatureData);
      enqueueSnackbar("Clinical record signed off successfully", {
        variant: "success",
      });
      if (patientId) getAllClinicalRecords(patientId);
      setOpenSignatureDialog(false);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to sign off clinical record", { variant: "error" });
    } finally {
      setSigningOffId(null);
    }
  };

  const handleRowClick = useCallback(
    (prescreeningId: number, id: any) => {
      const selectedRecord = clinicalRecords.find((record) => record.id === id);

      if (selectedRecord) {
        if (selectedRecord.type === "Contact Lenses") {
          setSelectedType("contact_lenses");
        } else if (selectedRecord.type === "Eye Examination") {
          setSelectedType("eye_examination");
        } else if (selectedRecord.type === "Diabetic Eye Screening" || selectedRecord.type === "MECS/CUES" || selectedRecord.type === "Glaucoma Referral Refinement") {
          setSelectedType("diabetic_eye_screening");
        }
      }
      setSelectedRow(id);
      getClinicalRecord(prescreeningId, id);
    },
    [clinicalRecords, getClinicalRecord]
  );

  // const getPatientInfo = (patientId: any) => {
  //   const patientService = new PatientService();
  //   patientService.getPatientName(patientId).then((res) => {
  //     setPatientInfo(res.data);
  //     console.log("Res: ",res);
  //   });
  // };

  useEffect(() => {
    if (clinicalRecords && clinicalRecords.length > 0) {
      getAllClinicsRecords();
      // getPatientInfo(patientId);
    }
  }, [clinicalRecords, getAllClinicsRecords, patientId]);
  return (
    <Stack
      alignItems="center"
      direction={{
        xs: "column",
        md: "row",
      }}
      sx={{
        borderRadius: "15px",
        position: "relative",
        boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.16)",
      }}
      p={2}
    >
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Table size={dense ? "small" : "medium"} sx={{ minWidth: "auto" }}>
          <Box
            sx={{
              p: 1,
              display: "flex",
              width: "100%",
            }}
          >
            <Box
              sx={{
                pl: 2,
                pr: 1,
                flexGrow: 1,
                width: "20%",
                mt: "20px",
              }}
            >
              <Stack spacing={0.1} direction="column" gap={2}>
                <Typography variant="body1" noWrap>
                  {patientInfo?.firstName}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ color: "text.secondary" }}
                >
                  {patientInfo?.postCode}
                </Typography>
                <Typography
                  variant="subtitle1"
                  noWrap
                  sx={{ color: "text.secondary" }}
                >
                  {fDate(patientInfo?.date_of_birth ?? null)}
                </Typography>
              </Stack>
            </Box>
            <Box
              sx={{
                borderRadius: "15px",
                boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.16)",
                maxHeight: "200px",
                width: "80%",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: "auto" }}>
                <TableHeadCustom headLabel={TABLE_HEAD} />
                <TableBody>
                  {clinicalRecords.map((item: any) => (
                    <TableRow
                      key={item.id}
                      sx={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedRow === item.id ? "#e0e0e0" : "inherit",
                      }}
                      onClick={() => {
                        setShowCLForm(false);
                        handleRowClick(item.prescreeningId, item.id);
                        getRefractionRecords(item.refractionId, item.id);
                        getHistoryRecords(item.symptomsId, item.id);
                        getExaminationRecords(item.examinationId, item.id);
                        getHandoverRecords(item.handoverId, item.id);
                        getAnteriorEyeRecords(item.anteriorEyeId, item.id);
                        getAllCLHistoryAndSymptoms(
                          item.clHistoryAndSymptomsId,
                          item.id
                        );
                        getCLFittingRecords(item.contactLensFittingId, item.id);
                        getTrailsRecords(item.id);
                        getCLAftercareRecords(
                          item.contactLensAfterCareId,
                          item.id
                        );
                        getCLHandoverRecords(
                          item.contactLensHandoverId,
                          item.id
                        );
                        getTeachScreenRecords(item.teachScreenId, item.id);
                        getDESRecords(item.diabeticEyeScreeningId, item.id);
                        getMECSRecords(item.mecsCuesId, item.id);
                        getGRRRecords(item.glaucomaReferralRefinementId, item.id);
                      }}
                    >
                      <TableCell align="left">
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <CustomAvatar name={`${item.id}`} />
                        </Stack>
                      </TableCell>
                      <TableCell align="left">
                        {item?.type === 'Diabetic Eye Screening' ? 'Diabetic Screening' :
                          item?.type === 'MECS/CUES' ? 'MECS/CUES' :
                            item?.type === 'Glaucoma Referral Refinement' ? 'Glaucoma Refinement' :
                              item?.prescreening?.reasonForVisit
                                ?.split(",")
                                ?.map((item: any) => item.trim())
                                ?.filter((item: any) => item)
                                ?.join(", ") ||
                              item?.contactLensHistoryAndSymptoms?.reasonForVisit
                                ?.split(",")
                                ?.map((item: any) => item.trim())
                                ?.filter((item: any) => item)
                                ?.join(", ") || "N/A"}
                      </TableCell>
                      <TableCell align="left">{item.type}</TableCell>
                      <TableCell align="left">
                        {formatDate(item.createdDate)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableNoData isNotFound={clinicalRecords.length <= 0} />
                </TableBody>
              </Table>
            </Box>
          </Box>
          <Scrollbar>
            {selectedType === "eye_examination" && (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 1 }}
                >
                  <Tabs value={eyeExamination} onChange={handleChildChange}>
                    {EYE_EXAMINATION_TABS.map((head) => (
                      <Tab
                        key={head.id}
                        value={head.id}
                        label={getTabLabel(head, 'eye_examination')}
                        sx={{ ml: "80px" }}
                      />
                    ))}
                  </Tabs>
                </Stack>
                {showEyeExaminationTabs()}
              </>
            )}
            {selectedType === "contact_lenses" && (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 1 }}
                >
                  <Tabs value={contactLenses} onChange={handleContactLensChange}>
                    {CONTACT_LENSES_TABS.map((head) => (
                      <Tab
                        key={head.id}
                        value={head.id}
                        label={getTabLabel(head, 'contact_lenses')}
                        sx={{ ml: "80px" }}
                      />
                    ))}
                  </Tabs>
                </Stack>
                {showContactLensesTabs()}
              </>
            )}
            {selectedType === "diabetic_eye_screening" && (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 1 }}
                >
                  <Tabs
                    value={diabeticEyeScreening}
                    onChange={handleDiabeticEyeScreeningChange}
                  >
                    {CLINICAL_PATHWAY_TABS.map((head) => (
                      <Tab
                        key={head.id}
                        value={head.id}
                        label={getTabLabel(head, 'diabetic_eye_screening')}
                        sx={{ ml: "80px" }}
                      />
                    ))}
                  </Tabs>
                </Stack>
                {showDiabeticEyeScreeningTabs()}
              </>
            )}
          </Scrollbar>
        </Table>
      </Box>
      {/* Signature Dialog */}
      <SignatureDialog
        open={openSignatureDialog}
        onClose={() => setOpenSignatureDialog(false)}
        onConfirm={handleSignOffConfirm}
        loading={!!signingOffId}
      />

      {/* Delayed Patient Notes Dialog */}
      <Dialog
        open={openNotesDialog}
        onClose={handleCloseNotesDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Patient Notes</DialogTitle>
        <DialogContent>
          <Typography>{notesContent}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotesDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
